import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Ticket, Plus, Copy, Check, Trash2, Power } from "lucide-react";
import { AdminTabs } from "@/components/AdminTabs";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";

const monthLabel = (m) => {
  const [y, mo] = (m || "").split("-");
  if (!y || !mo) return m;
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString(undefined, { month: "short", year: "numeric" });
};

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString() : "—");
const emptyForm = { name: "", code: "", trial_days: 30, max_uses: "", expiry_date: "", active: true };

export default function AdminPromos() {
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({ trials_started: 0, converted: 0 });
  const [monthly, setMonthly] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [list, analytics] = await Promise.all([
        api.get("/admin/promo-codes"),
        api.get("/admin/promo-analytics"),
      ]);
      setItems(list.data.items);
      setTotals(list.data.totals || { trials_started: 0, converted: 0 });
      setMonthly((analytics.data.monthly || []).map((r) => ({ ...r, label: monthLabel(r.month) })));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = {
        name: form.name,
        code: form.code.trim() || undefined,
        trial_days: parseInt(form.trial_days) || 30,
        max_uses: form.max_uses ? parseInt(form.max_uses) : null,
        expiry_date: form.expiry_date || null,
        active: form.active,
      };
      await api.post("/admin/promo-codes", payload);
      toast.success("Promo code created");
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setCreating(false);
    }
  };

  const toggle = async (p) => {
    try {
      await api.patch(`/admin/promo-codes/${p.id}`, { active: !p.active });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete promo code ${p.code}? This can't be undone.`)) return;
    try {
      await api.delete(`/admin/promo-codes/${p.id}`);
      toast.success("Promo code deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const copyLink = async (p) => {
    try {
      await navigator.clipboard.writeText(p.link);
      setCopied(p.id);
      toast.success("Promo link copied");
      setTimeout(() => setCopied(null), 2000);
    } catch {
      toast.error("Couldn't copy — copy manually: " + p.link);
    }
  };

  return (
    <div className="container-x py-12" data-testid="admin-promos">
      <AdminTabs />
      <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#CC5A3A] mb-2">Marketing</div>
      <h1 className="font-heading text-3xl text-[#1E3F33] font-semibold flex items-center gap-2">
        <Ticket className="w-6 h-6 text-[#2D6A4F]" /> Promo trial links
      </h1>
      <p className="text-[#5C6B64] mt-2 max-w-2xl">
        Create shareable links that give new collectors a free trial at sign-up — no card required. Perfect for launch campaigns and promotions. When the trial ends, collectors are prompted to subscribe.
      </p>

      <div className="grid grid-cols-3 gap-4 mt-6 max-w-xl" data-testid="promo-conversion-summary">
        <div className="rounded-2xl border border-[#1E3F33]/10 bg-white p-5" data-testid="promo-stat-trials">
          <div className="text-2xl font-heading text-[#1E3F33]">{totals.trials_started}</div>
          <div className="text-xs uppercase tracking-wider text-[#5C6B64] mt-1">Trials started</div>
        </div>
        <div className="rounded-2xl border border-[#1E3F33]/10 bg-white p-5" data-testid="promo-stat-converted">
          <div className="text-2xl font-heading text-[#2D6A4F]">{totals.converted}</div>
          <div className="text-xs uppercase tracking-wider text-[#5C6B64] mt-1">Converted to paid</div>
        </div>
        <div className="rounded-2xl border border-[#1E3F33]/10 bg-white p-5" data-testid="promo-stat-rate">
          <div className="text-2xl font-heading text-[#CC5A3A]">{totals.trials_started ? Math.round((totals.converted / totals.trials_started) * 100) : 0}%</div>
          <div className="text-xs uppercase tracking-wider text-[#5C6B64] mt-1">Conversion rate</div>
        </div>
      </div>

      {monthly.length > 0 && (
        <div className="mt-6 rounded-2xl border border-[#1E3F33]/10 bg-white p-6" data-testid="promo-conversion-chart">
          <h2 className="font-heading text-lg text-[#1E3F33] mb-4">Trials vs. conversions by month</h2>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={monthly} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E3F3315" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#5C6B64" }} tickLine={false} axisLine={{ stroke: "#1E3F3320" }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#5C6B64" }} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #1E3F3320", fontSize: 13 }} />
              <Legend wrapperStyle={{ fontSize: 13 }} />
              <Bar dataKey="trials" name="Trials started" fill="#2D6A4F" radius={[4, 4, 0, 0]} />
              <Bar dataKey="converted" name="Converted to paid" fill="#CC5A3A" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <form onSubmit={create} className="mt-8 rounded-2xl border border-[#1E3F33]/10 bg-white p-6" data-testid="promo-create-form">
        <h2 className="font-heading text-lg text-[#1E3F33] mb-4">Create a promo code</h2>
        <div className="grid md:grid-cols-3 gap-4">
          <label className="block md:col-span-1">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#5C6B64]">Campaign name</span>
            <input data-testid="promo-name" required className="input-soft mt-1.5" placeholder="Registration Campaign" value={form.name} onChange={set("name")} />
          </label>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#5C6B64]">Code <span className="normal-case font-normal text-[#8A9A86]">(blank = auto)</span></span>
            <input data-testid="promo-code" className="input-soft mt-1.5 uppercase" placeholder="LAUNCH30" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} />
          </label>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#5C6B64]">Trial days</span>
            <input data-testid="promo-trial-days" type="number" min="1" max="365" className="input-soft mt-1.5" value={form.trial_days} onChange={set("trial_days")} />
          </label>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#5C6B64]">Max uses <span className="normal-case font-normal text-[#8A9A86]">(blank = unlimited)</span></span>
            <input data-testid="promo-max-uses" type="number" min="1" className="input-soft mt-1.5" placeholder="Unlimited" value={form.max_uses} onChange={set("max_uses")} />
          </label>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#5C6B64]">Expires on <span className="normal-case font-normal text-[#8A9A86]">(blank = never)</span></span>
            <input data-testid="promo-expiry" type="date" className="input-soft mt-1.5" value={form.expiry_date} onChange={set("expiry_date")} />
          </label>
          <label className="flex items-center gap-2 mt-6">
            <input data-testid="promo-active" type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-4 h-4 accent-[#2D6A4F]" />
            <span className="text-sm text-[#1E3F33]">Active</span>
          </label>
        </div>
        <button data-testid="promo-create-button" disabled={creating} className="btn-primary mt-5 inline-flex items-center gap-2 disabled:opacity-50">
          <Plus className="w-4 h-4" /> {creating ? "Creating…" : "Create promo code"}
        </button>
      </form>

      <div className="mt-8">
        {loading ? (
          <div className="text-[#5C6B64]">Loading…</div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center text-center text-[#8A9A86] py-16 border border-dashed border-[#1E3F33]/15 rounded-2xl">
            <Ticket className="w-8 h-8 mb-3" />
            <p className="text-sm">No promo codes yet. Create one above to start a campaign.</p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-[#1E3F33]/10">
            <table className="w-full text-sm" data-testid="promos-table">
              <thead className="bg-[#F0EFEA] text-[#1E3F33] text-left">
                <tr>
                  <th className="px-4 py-2.5 font-medium">Campaign</th>
                  <th className="px-4 py-2.5 font-medium">Code</th>
                  <th className="px-4 py-2.5 font-medium">Trial</th>
                  <th className="px-4 py-2.5 font-medium">Uses</th>
                  <th className="px-4 py-2.5 font-medium">Converted</th>
                  <th className="px-4 py-2.5 font-medium">Expires</th>
                  <th className="px-4 py-2.5 font-medium">Status</th>
                  <th className="px-4 py-2.5 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p.id} className="border-t border-[#1E3F33]/8" data-testid={`promo-row-${p.id}`}>
                    <td className="px-4 py-2.5 font-medium text-[#1E3F33]">{p.name}</td>
                    <td className="px-4 py-2.5"><span className="text-xs font-mono px-2 py-0.5 rounded bg-[#1E3F33]/8 text-[#1E3F33]">{p.code}</span></td>
                    <td className="px-4 py-2.5 text-[#5C6B64]">{p.trial_days}d</td>
                    <td className="px-4 py-2.5 text-[#5C6B64]">{p.uses}{p.max_uses ? ` / ${p.max_uses}` : ""}</td>
                    <td className="px-4 py-2.5 text-[#5C6B64]" data-testid={`promo-converted-${p.id}`}>
                      {p.converted || 0}/{p.trials_started || 0}
                      {p.trials_started ? <span className="text-xs text-[#2D6A4F] ml-1">({Math.round((p.converted / p.trials_started) * 100)}%)</span> : null}
                    </td>
                    <td className="px-4 py-2.5 text-[#5C6B64]">{fmtDate(p.expiry_date)}</td>
                    <td className="px-4 py-2.5">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${p.active ? "bg-[#2D6A4F]/12 text-[#2D6A4F]" : "bg-[#8A9A86]/15 text-[#8A9A86]"}`}>
                        {p.active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-1.5">
                        <button type="button" data-testid={`promo-copy-${p.id}`} onClick={() => copyLink(p)} title="Copy link" className="p-2 rounded-lg hover:bg-[#1E3F33]/8 text-[#1E3F33]">
                          {copied === p.id ? <Check className="w-4 h-4 text-[#2D6A4F]" /> : <Copy className="w-4 h-4" />}
                        </button>
                        <button type="button" data-testid={`promo-toggle-${p.id}`} onClick={() => toggle(p)} title={p.active ? "Deactivate" : "Activate"} className="p-2 rounded-lg hover:bg-[#1E3F33]/8 text-[#1E3F33]">
                          <Power className="w-4 h-4" />
                        </button>
                        <button type="button" data-testid={`promo-delete-${p.id}`} onClick={() => remove(p)} title="Delete" className="p-2 rounded-lg hover:bg-[#E63946]/10 text-[#E63946]">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
